import { useEffect, useRef, useState } from "react";
import { ChecoutInitResponse } from "../components/CheckoutPayCard";
import { baseUrl } from "./configHelper";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../store";
import { useWindowSize } from "./useWindowSize";
import { bindActionCreators } from "redux";
import * as headerFooterStore from '../store/HeaderFooterStore';
import * as basketActions from '../store/BasketStore';
import { useHistory } from "react-router";
import { MopCheckoutRecord } from "./checkoutHelper";

declare global {
    interface Window {
        challengeIframe: any
        checkout: any
        isCalling: boolean
        nextAction?: WorldpayAction
        onError?: (err: string) => void
    }
}

interface WorldpayAction {
    href: string; method: string
}
interface WorldpayResult {
    outcome: "authorized" | string 
    authCode?: string | null
    refusalCode?: string | null
    actions?: {
        supply3dsDeviceData?: WorldpayAction
        complete3dsChallenge?: WorldpayAction
    }    
    deviceDataCollection?: {
        jwt?: string; url?: string; bin?: string;
    }
    challenge?: {
        reference: string; url: string; jwt: string; payload: string
    }
}

interface WorldpayCheckoutResponse{
    checkoutRecord: MopCheckoutRecord
    paymentResult: WorldpayResult
}

export const IsSuccess = (outcome: string) => {
    if(outcome == "authorized" || outcome == "sentForSettlement" || outcome == "Sent for Settlement"){
        return true;
    } else {
        return false;
    }
}

const useWorldPay = () => {

    const _onError = (error: string) => {
        unlockUi();
        window.onError && window.onError(error);
        if(!window.onError){
            console.log("No error handler to unlock UI", error);
        }
    }

    const _onSuccess = (res: WorldpayCheckoutResponse) => {
        console.log("Payment success");
        window.addToStatus("Worldpay response success");
        unlockUi();
        onSuccess.current && onSuccess.current(res); // pass in when CreatePayment is called, used by applepay to end device session before navigating away
        if(res?.checkoutRecord?.orderNumber){            
            updateOrderNumber(res?.checkoutRecord?.orderNumber);
        }        
        updatePaymentStatus(basketActions.PaymentStatus.COMPLETE);
        updateTempBasket(() => {
            clearBasket();
        })
        history.push(`/payment/success`);
    }

    const onSuccess = useRef<(res: WorldpayCheckoutResponse) => void>(); 
    //const onError = useRef<(msg: string) => void | undefined>();

    const checkoutRef = useRef<any>();
    const appState = useSelector((state: ApplicationState) => state);
    const { height, width } = useWindowSize();
    const [isPageLoading, setIsPageLoading] = useState(false);
    const setShowBack = bindActionCreators(headerFooterStore.actionCreators.setShowBack, useDispatch());
    const clearBasket = bindActionCreators(basketActions.actionCreators.clearBasketItems, useDispatch());
    const updateOrderNumber = bindActionCreators(basketActions.actionCreators.updateOrderNumber, useDispatch());
    const updatePaymentStatus = bindActionCreators(basketActions.actionCreators.updatePaymentStatus, useDispatch());
    const updateTempBasket = bindActionCreators(basketActions.actionCreators.updateTempBasket, useDispatch());
    const history = useHistory();

    const lockUi = () => {
        setShowBack(false);
    }

    const unlockUi = () => {
        setShowBack(true);
    }

    const initCheckoutSdk = (onReady?: () => void) => {
        fetch(baseUrl()+'/api/checkout/InitCheckoutv2')
            .then(response => response.json() as Promise<ChecoutInitResponse>)
            .then(data => {
                const isTest = data.isTest == true;
                if(!data.checkoutId){
                    console.log("Unable to initialize checkout, checkoutId missing")
                    return;
                }
                setIsPageLoading(false);
                if(!window.Worldpay){       
                    const script = document.createElement('script');

                    script.src = isTest
                        ? "https://try.access.worldpay.com/access-checkout/v2/checkout.js"
                        : "https://access.worldpay.com/access-checkout/v2/checkout.js";

                    script.async = true;

                    script.onload = () => {
                        console.log("Worldpay loaded");

                        if (window.Worldpay) {
                            InitWorldpay(data.checkoutId ?? "", onReady);
                        } else {
                            console.log("Worldpay library not found within src script");
                        }
                    };

                    script.onerror = () => {
                        console.error("Failed to load Worldpay script");
                    };

                    document.body.appendChild(script);
                }
                else {
                    InitWorldpay(data.checkoutId, onReady);
                }
            })
    }

    useEffect(() => {
        window.hideStepUpScreen = () => {
            console.log("3DS Challenge complete");
            const iframe = window.challengeIframe;
            if(iframe){
                iframe.remove();
                window.challengeIframe = null;
            } else {
                console.log("Cannot find challenge iframe to remove");
            }
            callNextAction();
        }
    }, []);

    // listen for deviceDataCollection response message
    useEffect(() => {
        const handleMessage = (event: MessageEvent<any>) => {
            const allowedOrigins = [
                'https://centinelapistag.cardinalcommerce.com',
                'https://centinelapi.cardinalcommerce.com'
            ];

            if (!allowedOrigins.includes(event.origin)) return;

            try {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                if (data.MessageType === 'profile.completed' && data.Status) {
                    console.log('Device data collection completed. SessionId:', data.SessionId);
                    callNextAction({ collectionReference: data.SessionId });
                }
            } catch (err) {
                console.error('Invalid message data', err);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    const callNextAction = (body?: any) => {

        if(window.isCalling){
            console.log("callNextAction already being called, skipping duplicate");
            return;
        }

        if(!window.nextAction){
            console.log("callNextAction aborted due to nextAction being empty");
            return;
        }

        console.log("calling next action with body", body, "and action", window.nextAction);
        window.isCalling = true;

        fetch(baseUrl() + '/api/checkout/resumepayment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                action: { ...window.nextAction },
                body: body
            })
        })
        .then(res => res.json())
        .then(res => {
            handlePaymentResponse(res);
        })
        .catch(err => {
            console.log("Resume payment error", err);
            _onError(err);
        }).finally(() => {
            console.log("clearing nextAction");
            window.isCalling = false;
        });
    }

    // load Worldpay library
    const InitWorldpay = (checkoutId: string, onReady?: () => void) => {
        const Worldpay = window.Worldpay;
        Worldpay.checkout.init(
            {
                id: checkoutId,
                form: "#card-form",
                fields: {
                    pan: {
                        selector: "#card-pan",
                        placeholder: "4444 3333 2222 1111"
                    },
                    expiry: {
                        selector: "#card-expiry",
                        placeholder: "MM/YY"
                    },
                    cvv: {
                        selector: "#card-cvv",
                        placeholder: "123"
                    }
                },
                styles: {
                    "input": {
                        "color": "black",
                        "font-size": "16px",
                        "font-family": "Arial",
                        "letter-spacing": "2px"
                    },
                    "input#pan": {
                        "font-size": "16px"
                    },
                    "input.is-valid": {
                        "color": "green"
                    },
                    "input.is-invalid": {
                        "color": "red"
                    },
                    "input.is-onfocus": {
                        "color": "black"
                    }
                },
                accessibility: {
                    lang: {
                        locale: "en-GB"
                    },
                    title: {
                        enabled: true,
                        pan: "PAN",
                        expiry: "Expiry Date",
                        cvv: "CVV"
                    }
                },
                acceptedCardBrands: ["amex", "diners", "discover", "jcb", "maestro", "mastercard", "visa"],
                enablePanFormatting: true
            },
            function (error: any, checkout: any) {
                if (error) {
                    console.error(error);
                    return;
                }

                window.checkout = checkout;
                checkoutRef.current = checkout;
                onReady && onReady();

                /*
                clear.addEventListener("click", function(event) {
                    event.preventDefault();
                    checkout.clearForm(function() {
                    console.log('Form successfully cleared');
                    });
                });
                */
            }
        );
    }

    const CreatePayment = async (props: { 
        name?: string, 
        sessionHref?: any,
        walletToken?: string
        type: "googlepay" | "applepay" | "card"
        onSuccess?: (res: WorldpayCheckoutResponse) => void
        onError?: (err: string) => void
    }) => {
        try{
            window.onError = props.onError; // the error handler is asigned here so it can be used in case either payment API call error's and ensures the specific page's error handler is used correctly
            onSuccess.current = props.onSuccess;
            console.log("setting onError to", props.onError);

            lockUi();
            const apiRes = await fetch(baseUrl() + "/api/checkout/CreatePaymentv2", {
                method: "POST", 
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    ...props,
                    email: (appState.basket?.email && appState.basket.email.length > 0) ? appState.basket.email :  "william.carpenter@hwdevelopment.eu",
                    basket: appState.basket?.basket ?? { totalPrice: 2.50 },
                    channelIdentifier: appState.location?.channelIdentifier,
                    screenWidth: width >= 600 ? 600 : width - 40,
                    screenHeight: height >= 1200 ? 1200 : height - 40,
                    returnUrl: window.location.origin + baseUrl() + "/callback",
                    userAgent: navigator.userAgent
                })
            })

            if(!apiRes.ok){
                const err = await apiRes.text();
                throw new Error("Unexpected Error during payment, please try again [ERR:CR2]");
            }

            const res = await apiRes.json() as WorldpayCheckoutResponse;
            handlePaymentResponse(res);
            return res;
        } catch(err: any){
            console.log("CreatePayment error", err);
            _onError(err.message);
            return false;
        }
    }

    const handlePaymentResponse = (checkoutResponse: WorldpayCheckoutResponse) => {
        const res = checkoutResponse.paymentResult;
        console.log("CreatePayment response:", res);


        if(IsSuccess(res.outcome)){
            _onSuccess(checkoutResponse);            
        } else if(res.outcome == "3dsDeviceDataRequired"){
            console.log("3DS required");
            if(res.deviceDataCollection){
                const action = res.actions?.supply3dsDeviceData;
                window.nextAction = action; // contains href and method used to submit deviceDataCollection response message

                const { jwt, url, bin } = res.deviceDataCollection;
                const formHtml = `
                    <html>
                    <body>
                        <form method="POST" action="${url}">
                            <input type="hidden" name="Bin" value="${bin}" />
                            <input type="hidden" name="JWT" value="${jwt}" />
                        </form>
                        <script>
                            document.forms[0].submit();
                        </script>
                    </body>
                    </html>
                `;

                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                iframe.width = '1';
                iframe.height = '1';
                iframe.srcdoc = formHtml;
                document.body.appendChild(iframe);
            } else {
                console.log("No deviceDataCollection in response");
            }
        } else if(res.outcome == "3dsChallenged") {
            const action = res.actions?.complete3dsChallenge;
            console.log("setting nextAction", action);
            window.nextAction = action; // contains href and method used to submit challenge outcome

            const { url, jwt } = res.challenge ?? {};

            const iframe = document.createElement('iframe');
            //iframe.width = `${width}`;
            //iframe.height = `${height}`;
            //iframe.srcdoc = formHtml;
            iframe.name = "challenge_iframe";
            iframe.style.border = 'none';
            iframe.style.position = "absolute";
            iframe.style.top = "0";
            iframe.style.left = "0";
            iframe.style.zIndex = "99";
            iframe.style.width = "100%";
            iframe.style.height = "100%";
            window.challengeIframe = iframe;
            

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = url as string;
            form.target = iframe.name;

            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'JWT';
            input.value = jwt as string;
            form.appendChild(input);

            const root = document.getElementById('root-app');
            root?.appendChild(form);
            root?.appendChild(iframe);

            // submit then remove 3DS challenge form which activates iframe
            form.submit();
            form.remove();
        } 
        else {
            console.log("Payment issue", res.outcome);
            var code = "PAYERR";
            switch(res.outcome){
                case "3dsAuthenticationFailed":
                    code = "3DSAUTH"
                    break;
                case "3dsUnavailable":
                    code = "3DSDOWN"
                    break;
                case "refused":
                    code = "REFUSED"
                    break;
            }
            if(res.refusalCode == "65"){
                // 3DS error (Google Pay)
                let message = "This payment method requires 3D Secure verification, which isn’t supported. ";
                
                if (window.ApplePaySession && ApplePaySession.canMakePayments()) {
                    message += "Please try again using Apple Pay or a regular card.";
                } else {
                    message += "Please try again using a different card or payment method.";
                }

                _onError(message);
            } else {
                _onError(`Payment request error, please try again or use a different payment method (${code})`)
            }
            
        }
    }

    return {
        initCheckoutSdk,
        checkoutRef,
        CreatePayment,
        isPageLoading
    }
}

export default useWorldPay;